import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';

export const useCreateContractPDF = () => {
  const { responseStreamHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useCreateContractPDF');

  return useMutation<Blob, DefaultError, string>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: 'PDF',
        })
      );
      const formData = new FormData();

      formData.append('contractId', body);

      const response = await apiClient({
        method: 'POST',
        url: `Contract/PDF`,
        body: formData,
        withContentType: false,
      });

      return responseStreamHandler(response);
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: 'PDF',
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetContractPdfFile = (id: string, enabled = true) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ['contract-pdf-file', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/${id}/PdfFile`,
      });

      const blob = await responseStreamHandler(response);

      if (blob.type === 'application/pdf') return blob;
      return new Blob([blob], { type: 'application/pdf' });
    },
    enabled: !!id && enabled,
    retry: false,
  });
};

export const useDeleteContractPdfFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteContractPdfFile');

  return useMutation<
    unknown,
    DefaultError,
    { id: string; version?: number }
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.remove.loading', {
          what: 'PDF',
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Contract/${body.id}/PdfFile`,
        params: body.version != null ? { version: body.version } : undefined,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `useDeleteContractPdfFile - ${body.id}`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: 'PDF',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostContractPdfFile = () => {
  const { responseStreamHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostContractPdfFile');

  return useMutation<Blob, DefaultError, { id: string; file: File; version: number }>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: 'PDF',
        })
      );

      const formData = new FormData();
      formData.append('file', body.file);
      formData.append('version', body.version.toString());

      const response = await apiClient({
        method: 'POST',
        url: `Contract/${body.id}/PdfFile`,
        body: formData,
        withContentType: false,
      });

      return responseStreamHandler(response);
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: 'PDF',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutContractPdfFile = () => {
  const { responseStreamHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutContractPdfFile');

  return useMutation<Blob, DefaultError, { id: string; file: File; version: number }>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: 'PDF',
        })
      );

      const formData = new FormData();
      formData.append('file', body.file);
      formData.append('version', body.version.toString());

      const response = await apiClient({
        method: 'PUT',
        url: `Contract/${body.id}/PdfFile`,
        body: formData,
        withContentType: false,
      });

      return responseStreamHandler(response);
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: 'PDF',
        })
      );
    },
    onError: dismiss,
  });
};
